import Login from '@shared/views/auth/login'
import Keycloak2FAPopup from '@shared/components/Keycloak2FAPopup'
import Broadcast2FA from '@shared/components/Broadcast2FA'
import { useAuthStore } from '@shared/store/auth'
import { useLocationStore } from '@shared/store/location'
import { usesKeycloakLogin } from '@shared/constants'

export async function requireAuth() {
  const authStore = useAuthStore()
  await authStore.waitThInitializated()

  if (!authStore.isAuthenticated) {
    return '/login'
  }
}

export const commonRoutes = [
  {
    path: '/login',
    component: Login,
    beforeEnter() {
      // To check IE solution came from:
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
      if (window?.navigator?.userAgent?.indexOf('Trident') > -1) {
        return '/browser'
      }
      if (useAuthStore().isAuthenticated) {
        return '/'
      }
    }
  },
  {
    path: '/2fa_required',
    component: Keycloak2FAPopup
  },
  {
    path: '/2fa_broadcast',
    component: Broadcast2FA
  },
  {
    path: '/401',
    component: () =>
      import(/* webpackChunkName: "errors_401" */ '@shared/views/errors/401')
  },
  {
    path: '/403',
    component: () =>
      import(/* webpackChunkName: "errors_403" */ '@shared/views/errors/403')
  },
  {
    path: '/404',
    component: () =>
      import(/* webpackChunkName: "errors_404" */ '@shared/views/errors/404')
  },
  {
    path: '/500',
    name: '500',
    component: () =>
      import(/* webpackChunkName: "errors_500" */ '@shared/views/errors/500'),
    props: true
  },
  {
    path: '/504',
    component: () =>
      import(/* webpackChunkName: "errors_504" */ '@shared/views/errors/504')
  },
  {
    path: '/browser',
    component: () =>
      import(
        /* webpackChunkName: "errors_browser" */ '@shared/views/errors/browser'
      )
  },
  {
    path: '/login/password_reset/:password_reset_id',
    name: 'password-reset',
    component: () =>
      import(
        /* webpackChunkName: "create-password" */ '@shared/views/auth/create-password'
      )
  },
  {
    path: '/password_forgotten',
    component: () =>
      import(
        /* webpackChunkName: "auth_password_forgotten" */ '@shared/views/auth/password-forgotten'
      )
  },
  {
    path: '/logout',
    component: () =>
      import(/* webpackChunkName: "auth_logout" */ '@shared/views/auth/logout')
  },
  {
    path: '/redirect',
    component: () =>
      import(/* webpackChunkName: "redirect" */ '@shared/components/redirect')
  },
  {
    path: '/notification/unsubscribe/:tenantId/:notificationId',
    component: () =>
      import(
        /* webpackChunkName: "notification-unsubscribe" */ '@shared/views/notification-unsubscribe'
      )
  },
  {
    path: '/login/support',
    component: () =>
      import(
        /* webpackChunkName: "auth_support" */ '@shared/views/auth/support'
      ),
    meta: {
      beforeResolve(routeTo) {
        // A support link opened while another session is stored must end that session first.
        // /logout revokes its token, resets every store and reloads the page into the link, so
        // nothing from the previous account survives into the one being logged into. Keycloak
        // sessions are exempt: support agents there keep their Keycloak session.
        if (useAuthStore().authenticated && !usesKeycloakLogin()) {
          return {
            path: '/logout',
            query: { redirect: routeTo.fullPath }
          }
        }

        useAuthStore().clearStateOnLogin()
        useLocationStore().$reset()
      }
    }
  }
]

export const noMatch = {
  path: '/:catchAll(.*)',
  component: () =>
    import(/* webpackChunkName: "errors_404" */ '@shared/views/errors/404')
}
